import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm, type Role } from '../api/types'
import { Banner, Button, ErrorBanner, Empty, Panel, Spinner, TextInput } from '@shop/ui'

/**
 * إدارةُ الصلاحيّات — <b>الدورُ وما يملكه</b>.
 *
 * <p>
 * ⚠ <b>والصلاحيّةُ تُمنح للدور لا للشخص.</b> ومنحُ فردٍ صلاحيّةً خارجَ دوره
 * يجعل السؤالَ «من يستطيع النشر؟» بلا جواب: لا تكفي قراءةُ الأدوار، بل تُقرأ
 * حساباتُ الموظّفين واحدًا واحدًا. وشاشةُ الموظّف تُسند الأدوار، وهذه تُعرّفها.
 * </p>
 *
 * <p>
 * ⚠ <b>ولا صلاحيّةَ تأتي ضِمنًا مع أخرى.</b> كلُّ رمزٍ هنا مربّعٌ مستقلّ:
 * <c>catalog.product.publish</c> تُمنح وتُسحب وحدَها، ولا يجرّها
 * <c>catalog.product.edit</c> معه. والسببُ في <c>docs/20</c> ق-٩: النشرُ قرارٌ
 * عمّا يراه الزبائن، وتعديلُ اسمٍ أو لونٍ عملٌ يوميّ — ومن يفعل الثاني ليس
 * بالضرورة من يقرّر الأوّل.
 * </p>
 *
 * <p>
 * ⚠ <b>ودورُ المالك لا يُقلَّم</b> (<c>identity.owner_role_immutable</c>):
 * تقليمُه يترك النظامَ بلا من يُصلحه.
 * </p>
 */
export default function Roles() {
  const { can } = useAuth()
  const [open, setOpen] = useState<string | null>(null)

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<Role[]>('/api/v1/roles'),
    enabled: can(Perm.roleView),
  })

  if (!can(Perm.roleView)) {
    return (
      <Panel title="إدارةُ الصلاحيّات">
        <Empty>لا تملك عرضَ الأدوار.</Empty>
      </Panel>
    )
  }

  return (
    <Panel title="إدارةُ الصلاحيّات">
      <p className="mb-4 text-xs text-ink-soft">
        الصلاحيّةُ تُمنح للدور، والدورُ يُسند للموظّف من صفحته.
        <b> وكلُّ صلاحيّةٍ مستقلّةٌ</b> — لا شيءَ يُمنح ضِمنًا مع غيره.
      </p>

      {roles.isPending && <Spinner />}
      {roles.isError && <ErrorBanner error={roles.error} />}

      <ul className="space-y-2">
        {(roles.data ?? []).map((role) => (
          <li key={role.id}>
            <button
              type="button"
              onClick={() => setOpen(open === role.id ? null : role.id)}
              className="block w-full rounded-md border border-line px-3 py-2 text-start hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{role.nameAr}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">
                  {role.code}
                </span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{role.permissionCodes.length} صلاحيّةً</span>
                {role.isSystem && <span>دورٌ من النظام</span>}
              </div>
            </button>

            {open === role.id && (
              <div className="mt-2">
                <RoleEditor role={role} mayEdit={can(Perm.roleEdit)} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** صلاحيّةٌ كما يعرفها الكود — <c>GET /api/v1/permissions</c>. */
type PermissionRow = {
  code: string
  nameAr: string
  module: string
  entity: string
  action: string
  isSensitive: boolean
}

/** أسماءُ الوحدات كما تُعرض — وما لا اسمَ له يُعرض برمزه لا يُخفى. */
const moduleNames: Record<string, string> = {
  catalog: 'الأصناف والكتالوج',
  identity: 'المستخدمون والأدوار',
  intake: 'مسوّدات الإدخال',
  inventory: 'المخزون والحركات',
  org: 'الفروع والمستودعات',
  parties: 'المورّدون والزبائن',
  purchasing: 'الشراء والاستلام',
  report: 'التقارير',
  sales: 'البيع والمرتجعات',
}

function RoleEditor({ role, mayEdit }: { role: Role; mayEdit: boolean }) {
  const queryClient = useQueryClient()
  const [term, setTerm] = useState('')

  // نسخةُ التحرير تُملأ مرّةً من الخادم — والمكوّنُ يُعاد بناؤه بـ`key` حين
  // يتغيّر الدور، فلا أثرَ يضبط حالة.
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(role.permissionCodes))

  const registry = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api<PermissionRow[]>('/api/v1/permissions'),
    staleTime: 30 * 60_000,
  })

  const save = useMutation({
    mutationFn: () =>
      api<Role>(`/api/v1/roles/${role.id}/permissions`, {
        method: 'PUT',
        body: { permissionCodes: [...chosen] },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })

  // ⚠ **والتجميعُ بالوحدة من الرمز نفسِه** — لا قائمةَ مكتوبةٌ هنا تُنسى حين
  //   تُضاف صلاحيّةٌ جديدة. وصلاحيّةٌ لا تظهر في الشاشة لا تُمنح أبدًا.
  const groups = useMemo(() => {
    const rows = (registry.data ?? []).filter((p) => {
      const needle = term.trim()

      return needle === '' || p.nameAr.includes(needle) || p.code.includes(needle)
    })

    const byModule = new Map<string, PermissionRow[]>()

    for (const row of rows) {
      const bucket = byModule.get(row.module)

      if (bucket) {
        bucket.push(row)
      } else {
        byModule.set(row.module, [row])
      }
    }

    return [...byModule.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [registry.data, term])

  const toggle = (code: string, on: boolean) => {
    setChosen((current) => {
      const next = new Set(current)

      if (on) {
        next.add(code)
      } else {
        next.delete(code)
      }

      return next
    })
  }

  const dirty =
    chosen.size !== role.permissionCodes.length
    || role.permissionCodes.some((code) => !chosen.has(code))

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      {role.description && <p className="mb-3 text-xs text-ink-soft">{role.description}</p>}

      {!mayEdit && (
        <div className="mb-3">
          <Banner tone="info">لا تملك تعديلَ الأدوار — هذه قراءةٌ فقط.</Banner>
        </div>
      )}

      <div className="mb-3">
        <TextInput value={term} onChange={setTerm} placeholder="ابحث في الصلاحيّات…" />
      </div>

      {registry.isPending && <Spinner />}
      {registry.isError && <ErrorBanner error={registry.error} />}

      <div className="space-y-4">
        {groups.map(([module, rows]) => (
          <section key={module}>
            <h3 className="mb-2 text-xs font-medium text-ink-soft">
              {moduleNames[module] ?? module}
            </h3>

            <div className="grid gap-2 sm:grid-cols-2">
              {rows.map((permission) => (
                <label
                  key={permission.code}
                  className="flex items-start gap-2 rounded-md border border-line bg-raised px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-brand"
                    checked={chosen.has(permission.code)}
                    disabled={!mayEdit}
                    onChange={(e) => toggle(permission.code, e.target.checked)}
                  />

                  <span>
                    {permission.nameAr}
                    {permission.isSensitive && (
                      <span className="mr-1 text-xs text-warn">· حسّاسة</span>
                    )}
                    <span className="block text-xs text-ink-soft" dir="ltr">
                      {permission.code}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>

      {save.isError && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}

      {save.isSuccess && !dirty && (
        <div className="mt-3">
          <Banner tone="ok">حُفظت. وكلُّ من يحمل هذا الدورَ يسري عليه فورًا.</Banner>
        </div>
      )}

      {mayEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button tone="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'يُحفظ…' : 'حفظُ الصلاحيّات'}
          </Button>

          <Button
            disabled={!dirty || save.isPending}
            onClick={() => setChosen(new Set(role.permissionCodes))}
          >
            تراجع
          </Button>

          <span className="text-xs text-ink-soft">{chosen.size} مختارة</span>
        </div>
      )}
    </div>
  )
}
